import {
  Music2, Play, Pause, SkipBack, SkipForward, Shuffle, Repeat, Repeat1,
  Volume2, VolumeX, ListMusic, ChevronDown, X,
} from "lucide-react";
import type { MusicPlayerState } from "./useMusicPlayer";

interface MusicPlayerProps {
  dir: "rtl" | "ltr";
  player: MusicPlayerState;
}

function fmtTime(secs: number): string {
  if (!isFinite(secs) || secs < 0) return "0:00";
  const m = Math.floor(secs / 60);
  const s = Math.floor(secs % 60);
  return `${m}:${String(s).padStart(2, "0")}`;
}

export function MusicPlayer({ dir, player }: MusicPlayerProps) {
  const ar = dir === "rtl";
  const {
    tracks, currentIdx, isPlaying, currentTime, duration,
    volume, muted, shuffle, repeat,
    showPlaylist, setShowPlaylist, fileInputRef,
    current, togglePlay, goTo, goPrev, goNext,
    handleSeek, handleVolume, toggleMute,
    cycleRepeat, setShuffle, handleFileChange, removeTrack,
  } = player;

  const progress = duration > 0 ? (currentTime / duration) * 100 : 0;

  const btnBase = "w-8 h-8 flex items-center justify-center rounded-xl transition-colors shrink-0";
  const btnGhost = `${btnBase} text-stone-400 dark:text-slate-500 hover:text-stone-700 dark:hover:text-slate-200 hover:bg-stone-100 dark:hover:bg-slate-700`;
  const btnActive = `${btnBase} text-violet-500 bg-violet-50 dark:bg-violet-500/15`;

  return (
    <div className="bg-pink-50 dark:bg-slate-800 rounded-2xl border border-pink-100 dark:border-slate-700 overflow-hidden" dir={dir}>

      {/* ── Compact bar (always visible) ── */}
      <div className="flex items-center gap-2 px-3 py-2.5">

        {/* Icon */}
        <div className="w-8 h-8 rounded-xl bg-gradient-to-br from-violet-500 to-pink-500 flex items-center justify-center shrink-0 shadow-sm">
          <Music2 style={{ width: 16, height: 16 }} className="text-white" />
          {isPlaying && (
            <span className="absolute w-2 h-2 rounded-full bg-violet-400 animate-pulse -top-0.5 -right-0.5" />
          )}
        </div>

        {/* Track name */}
        <div className="flex-1 min-w-0">
          {tracks.length > 0 ? (
            <>
              <p className="text-xs font-bold text-stone-800 dark:text-slate-100 truncate leading-tight">
                {current?.name ?? "—"}
              </p>
              <p className="text-[10px] text-stone-400 dark:text-slate-500 tabular-nums">
                {fmtTime(currentTime)} / {fmtTime(duration)}
              </p>
            </>
          ) : (
            <p className="text-xs text-stone-400 dark:text-slate-500">
              {ar ? "مشغل الموسيقى" : "Music Player"}
            </p>
          )}
        </div>

        {/* Prev */}
        <button onClick={goPrev} disabled={tracks.length === 0} aria-label={ar ? "المقطع السابق" : "Previous track"} className={`${btnGhost} disabled:opacity-30`}>
          <SkipBack className="w-4 h-4" />
        </button>

        {/* Play / Pause */}
        <button
          onClick={togglePlay}
          disabled={tracks.length === 0}
          aria-label={isPlaying ? (ar ? "إيقاف مؤقت" : "Pause") : (ar ? "تشغيل" : "Play")}
          className="w-9 h-9 rounded-xl bg-gradient-to-br from-violet-500 to-pink-500 text-white flex items-center justify-center shadow-sm hover:shadow-md transition-all hover:scale-105 active:scale-95 disabled:opacity-40 disabled:hover:scale-100 shrink-0"
        >
          {isPlaying ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4 ml-0.5" />}
        </button>

        {/* Next */}
        <button onClick={goNext} disabled={tracks.length === 0} aria-label={ar ? "المقطع التالي" : "Next track"} className={`${btnGhost} disabled:opacity-30`}>
          <SkipForward className="w-4 h-4" />
        </button>

        {/* Arrow → open file picker */}
        <button
          onClick={() => fileInputRef.current?.click()}
          aria-label={ar ? "إضافة ملفات موسيقى" : "Add music files"}
          title={ar ? "اختيار ملفات الموسيقى" : "Choose music files"}
          className={`${btnGhost} text-violet-500 hover:text-violet-600 hover:bg-violet-50 dark:hover:bg-violet-500/15`}
        >
          <ChevronDown className="w-4 h-4" />
        </button>

        <input
          ref={fileInputRef}
          type="file"
          accept="audio/*"
          multiple
          className="hidden"
          onChange={handleFileChange}
        />
      </div>

      {/* ── Seek bar ── */}
      {tracks.length > 0 && (
        <div className="px-3 pb-2 space-y-2">
          <input
            type="range"
            min={0}
            max={duration || 1}
            step={0.5}
            value={currentTime}
            onChange={e => handleSeek(Number(e.target.value))}
            className="w-full h-1.5 rounded-full accent-violet-500 cursor-pointer"
            style={{ background: `linear-gradient(to right, #8b5cf6 ${progress}%, #e5e7eb ${progress}%)` }}
          />

          {/* Secondary controls row */}
          <div className="flex items-center gap-1">
            <button onClick={cycleRepeat} aria-label={repeat === "one" ? (ar ? "تكرار مقطع واحد" : "Repeat one") : repeat === "all" ? (ar ? "تكرار" : "Repeat") : (ar ? "تكرار" : "Repeat")} className={`${repeat !== "none" ? btnActive : btnGhost} !w-7 !h-7`}>
              {repeat === "one" ? <Repeat1 className="w-3.5 h-3.5" /> : <Repeat className="w-3.5 h-3.5" />}
            </button>
            <button onClick={() => setShuffle(!shuffle)} aria-label={ar ? "تشغيل عشوائي" : "Shuffle"} className={`${shuffle ? btnActive : btnGhost} !w-7 !h-7`}>
              <Shuffle className="w-3.5 h-3.5" />
            </button>
            <button onClick={toggleMute} aria-label={muted || volume === 0 ? (ar ? "إلغاء كتم الصوت" : "Unmute") : (ar ? "كتم الصوت" : "Mute")} className={`${btnGhost} !w-7 !h-7`}>
              {muted || volume === 0 ? <VolumeX className="w-3.5 h-3.5" /> : <Volume2 className="w-3.5 h-3.5" />}
            </button>
            <input
              type="range" min={0} max={1} step={0.02}
              aria-label={ar ? "مستوى الصوت" : "Volume"}
              value={muted ? 0 : volume}
              onChange={e => handleVolume(Number(e.target.value))}
              className="flex-1 h-1 rounded-full accent-violet-500 cursor-pointer"
            />
            <button
              onClick={() => setShowPlaylist(!showPlaylist)}
              aria-label={ar ? "قائمة التشغيل" : "Playlist"}
              className={`${showPlaylist ? btnActive : btnGhost} !w-7 !h-7`}
            >
              <ListMusic className="w-3.5 h-3.5" />
            </button>
          </div>

          {/* Playlist */}
          {showPlaylist && (
            <div className="space-y-1 max-h-48 overflow-y-auto rounded-xl border border-stone-100 dark:border-slate-700 p-1">
              {tracks.map((track, i) => (
                <div
                  key={i}
                  onClick={() => goTo(i)}
                  className={`flex items-center gap-2 px-2.5 py-2 rounded-lg cursor-pointer transition-colors ${
                    i === currentIdx
                      ? "bg-violet-500 text-white"
                      : "hover:bg-stone-50 dark:hover:bg-slate-700 text-stone-700 dark:text-slate-300"
                  }`}
                >
                  <div className={`w-5 h-5 rounded-md flex items-center justify-center shrink-0 text-[10px] font-bold ${i === currentIdx ? "bg-white/25" : "bg-stone-100 dark:bg-slate-600"}`}>
                    {i === currentIdx && isPlaying
                      ? <span className="w-2 h-2 rounded-full bg-white animate-pulse" />
                      : <span>{i + 1}</span>}
                  </div>
                  <span className="flex-1 text-xs font-medium truncate">{track.name}</span>
                  <button
                    onClick={e => { e.stopPropagation(); removeTrack(i); }}
                    aria-label={ar ? "إزالة المقطع" : "Remove track"}
                    className={`w-5 h-5 flex items-center justify-center rounded transition-colors shrink-0 ${i === currentIdx ? "hover:bg-white/25 text-white/70" : "hover:bg-stone-200 dark:hover:bg-slate-600 text-stone-400"}`}
                  >
                    <X className="w-3 h-3" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
